/**
 * Block: Page top with product
 *
 * "Seisnode" node clips drift behind the centred heading. Each clip is shot on
 * black and composited with mix-blend-mode: screen (see the SCSS), so no alpha
 * channel is needed. We deliberately DON'T scrub frames — the videos just loop.
 *
 * With reveal copy the block is two screens tall and the scroll between them is
 * the transition: the nodes float up out of the frame, the hero copy goes the
 * same way a beat at a time but slower and fading as it goes, the scrim leaves
 * with them and the background blends from dark into the page colour, landing
 * on it exactly as the bottom edge of the section comes into view — so there is
 * never a seam to see. The reveal items fade up on their own trigger, keyed to
 * where they are on screen rather than to the section, which keeps them looking
 * right whatever the copy's length: the shortcuts row first, then the heading
 * and the body copy together a beat later. Everything is scrubbed off the
 * scroll position.
 *
 * Playback is gated on viewport visibility (IntersectionObserver) and tab
 * visibility to save battery, and skips nodes CSS has hidden (the phone layout
 * shows one node, not two) so we never fetch a clip nobody sees. Under
 * prefers-reduced-motion nothing plays or moves — the poster frame stands in
 * and the reveal copy is simply there. The block only runs on the front end;
 * the .twig omits data-ptwm in the editor so this is a no-op there.
 *
 * Assets are static theme files under /static/seisnode/, rebuilt with
 * bin/build-seisnode-video.sh.
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { intersectionObserve } from './_intersection-observer.js';

gsap.registerPlugin(ScrollTrigger);

// The nodes lift over a fixed screen of scroll, so how fast they leave doesn't
// depend on how much copy the reveal happens to hold. They travel their own full
// height (yPercent -100), which — on top of the page scrolling under them — gets
// the low-hanging one up past the reveal copy within the first ~100px, before
// that copy has faded up far enough to read over it. Beats are fractions of the
// range.
const LIFT = { end: '+=100%', travel: -100, nodes: [0, 0.6], scrim: [0, 0.35] };

// The hero copy leaves on that same range, but as a drift rather than a flight:
// a fraction of the nodes' travel, so it reads as the slower thing in the frame,
// and it fades on the way instead of being carried off by the scroll alone. In
// vh because these lines have no useful height of their own to be a percentage
// of — a heading that wraps to two lines shouldn't travel twice as far.
//
// The lines go top down, but only just — enough that the block loosens as it
// leaves rather than sliding off as one slab, not so much that you catch them
// going separately. Duration plus the stagger over three lines lands short of 1:
// the last of them is out before the range ends, leaving the tail of the scroll
// to the reveal copy coming up underneath.
const HERO_EXIT = { start: 0, travel: '-14lvh', duration: 0.5, stagger: 0.05 };

// Where the reveal items fade up: lines on the screen that the copy climbs past,
// as percentages of the viewport, so the beats read the same whatever the copy's
// length. `start` is the *lower* line on screen (the bigger number), because
// these travel up the viewport as you scroll and a range that ran the other way
// would be clamped to nothing (ScrollTrigger takes the max of the two) and snap
// instead of scrub. The gap between the two is what sets the pace — wide enough
// that each beat is one you can see when the shortcuts row shares the range with
// the copy, tight enough that the fade is well under way early in the scroll.
//
// Numbers, not ScrollTrigger strings: _revealLines pulls `start` up to where the
// copy rests when it has to, so nothing is part-way faded in before the first
// scroll.
const REVEAL_TRIGGER = { start: 70, end: 55 };

// One beat of the reveal, as a fraction of that range. Each beat both lasts and
// waits this long, so a beat is finished before the next one starts.
const REVEAL_BEAT = 0.5;

const reducedMotion = () =>
  !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

const isTransparent = (color) => !color || color === 'transparent' || /,\s*0\)$/.test(color);

class PageTopModel {
  constructor(section) {
    this.section = section;
    this.stage = section.querySelector('.page-top-with-model__stage');
    this.scrim = section.querySelector('.page-top-with-model__scrim');
    // Each node IS a <video> element (the block markup has no wrapper — a wrapper
    // would create a stacking context and break the screen blend; see the SCSS).
    this.videos = Array.from(section.querySelectorAll('[data-ptwm-node]'));
    // The hero lines themselves rather than the block holding them: they leave a
    // beat apart, and the wrapper is what centres them.
    this.heroItems = Array.from(section.querySelectorAll('.page-top-with-model__content > *'));
    this.reveal = section.querySelector('.page-top-with-model__reveal');
    this.revealItems = Array.from(section.querySelectorAll('[data-ptwm-reveal-item]'));

    this.inView = false;
    this.pageVisible = !document.hidden;
    this.mm = null;

    this._onVisibility = this._onVisibility.bind(this);
    this._updatePlayback = this._updatePlayback.bind(this);
  }

  init() {
    if (!this.videos.length) return;

    intersectionObserve(this.section, 0, 0, (el, ratio) => {
      this.inView = ratio > 0;
      this._updatePlayback();
    });

    document.addEventListener('visibilitychange', this._onVisibility);

    this.motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    if (this.motionQuery && this.motionQuery.addEventListener) {
      this.motionQuery.addEventListener('change', this._updatePlayback);
    }

    // matchMedia builds the scene only where motion is welcome, and reverts it —
    // inline styles and all — if the preference changes.
    this.mm = gsap.matchMedia();
    this.mm.add('(prefers-reduced-motion: no-preference)', () => this._buildScene());
  }

  // Only nodes the current layout actually shows: display:none leaves no
  // offsetParent, and playing one would pull down a clip nobody can see.
  _visibleVideos() {
    return this.videos.filter((v) => v.offsetParent !== null);
  }

  _updatePlayback() {
    this.videos.forEach((v) => v.pause());
    if (reducedMotion() || !this.pageVisible || !this.inView) return;

    this._visibleVideos().forEach((v) => {
      const p = v.play();
      if (p && p.catch) p.catch(() => {}); // ignore autoplay-policy rejections
    });
  }

  // The colour the section blends into: whatever is actually painted behind it,
  // so this lands on the page background in either theme. Falls back to the
  // token when every ancestor is transparent.
  _pageBackground() {
    for (let el = this.section.parentElement; el; el = el.parentElement) {
      const bg = getComputedStyle(el).backgroundColor;
      if (!isTransparent(bg)) return bg;
    }
    return getComputedStyle(document.documentElement).getPropertyValue('--body-bg-color').trim() || '#000032';
  }

  // The lines REVEAL_TRIGGER asks for, pulled up the screen if the copy already
  // sits below them before you have scrolled at all.
  //
  // The reveal rests --ptwm-hero vh down the section — 55vh on wide layouts,
  // where the hero band is cut short — and the section starts at the top of the
  // page, so that is also where the copy sits on screen at scroll 0. A start line
  // below it is a line the copy is already past, which would leave the fade
  // part-way through (or finished, if the end line is past too) on the first
  // paint, with no scroll behind it. Clamping the start to the resting line
  // instead means progress is exactly 0 until the page moves. The end follows it
  // down by the same distance, so the pace stays what it was tuned to.
  //
  // Measured as the gap between two bounding rects, which is the distance from
  // the section's top edge to the reveal's whatever the scroll position is when
  // we read it, and so doesn't care when a refresh happens to call this.
  _revealLines() {
    const rest =
      ((this.reveal.getBoundingClientRect().top - this.section.getBoundingClientRect().top) /
        window.innerHeight) *
      100;
    const start = Math.min(REVEAL_TRIGGER.start, rest);
    return {
      start: `top ${start}%`,
      end: `top ${start - (REVEAL_TRIGGER.start - REVEAL_TRIGGER.end)}%`,
    };
  }

  _buildScene() {
    const lift = gsap.timeline({
      defaults: { ease: 'none' },
      scrollTrigger: {
        trigger: this.section,
        start: 'top top',
        end: LIFT.end,
        scrub: 0.5,
      },
    });

    // Empty tween spanning the whole range: without it the timeline would be as
    // long as its last beat and the scrub would stretch to fill the scroll.
    lift.to({}, { duration: 1 }, 0);

    // Offset geometry rather than bounding rects: it is the untransformed
    // layout, so a rebuild measures the same whatever a reverted timeline left
    // behind.
    const stageWidth = this.stage ? this.stage.offsetWidth : 1;
    this.videos.forEach((video) => {
      // Corner nodes drift outward as they rise; a centred one (phone layout)
      // goes straight up.
      const centre = (video.offsetLeft + video.offsetWidth / 2) / stageWidth;
      lift.to(
        video,
        {
          yPercent: LIFT.travel,
          xPercent: (centre - 0.5) * 45,
          duration: LIFT.nodes[1] - LIFT.nodes[0],
        },
        LIFT.nodes[0]
      );
    });
    // No fade on the way out: the page scroll carries the nodes off the top and
    // the stage clips them well above the fold, so they keep their proper colour
    // the whole way. Fading them would take the yellow through olive on its way
    // to the navy behind.

    // The hero copy rides the same timeline, so it is in step with the nodes
    // without a trigger of its own to keep tuned. `to`, not `fromTo`: unlike the
    // reveal items below, these are on screen at rest and there is nothing for
    // the CSS to hold back — the scroll is what takes them away.
    if (this.heroItems.length) {
      lift.to(
        this.heroItems,
        {
          y: HERO_EXIT.travel,
          opacity: 0,
          duration: HERO_EXIT.duration,
          stagger: HERO_EXIT.stagger,
        },
        HERO_EXIT.start
      );
    }

    if (this.scrim) {
      lift.to(this.scrim, { autoAlpha: 0, duration: LIFT.scrim[1] - LIFT.scrim[0] }, LIFT.scrim[0]);
    }

    // The background is the one thing tied to the section itself: it has to
    // arrive at the page colour by the time the section's bottom edge reaches
    // the bottom of the viewport — the first moment the join with the next block
    // could be seen — or that join shows as a band of the wrong dark. That is
    // `bottom bottom`, written out so a section no taller than the viewport
    // still gets a range instead of a zero-length trigger.
    if (this.reveal) {
      gsap.to(this.section, {
        backgroundColor: this._pageBackground(),
        ease: 'none',
        scrollTrigger: {
          trigger: this.section,
          start: 'top top',
          end: () => `+=${Math.max(1, this.section.offsetHeight - window.innerHeight)}`,
          invalidateOnRefresh: true,
          scrub: 0.5,
        },
      });
    }

    // fromTo, not from: the SCSS already holds these at opacity 0 to stop them
    // flashing before this bundle runs, and a from() tween would read that as
    // the value to animate *to*.
    //
    // Two beats, not one per item: the shortcuts row first, then the heading and
    // the body copy together, on identical timing — the copy sits beside the
    // heading on the wide layout, so a beat of its own only made the pair read as
    // out of step. A beat is a full duration, so the row is up before the copy
    // starts. Durations are proportions here, not seconds — the scrub maps the
    // whole timeline onto REVEAL_TRIGGER's range, so the beats divide that range
    // between them however many there are.
    if (this.revealItems.length) {
      const isShortcuts = (el) => el.classList.contains('page-top-with-model__shortcuts');
      const beats = [
        this.revealItems.filter(isShortcuts),
        this.revealItems.filter((el) => !isShortcuts(el)),
      ].filter((group) => group.length);

      const reveal = gsap.timeline({
        defaults: { ease: 'none', duration: REVEAL_BEAT },
        scrollTrigger: {
          trigger: this.reveal,
          // Functions, so a resize or a breakpoint change re-measures the resting
          // line on refresh rather than keeping the one from build time.
          start: () => this._revealLines().start,
          end: () => this._revealLines().end,
          invalidateOnRefresh: true,
          scrub: 0.5,
        },
      });

      beats.forEach((group, i) => {
        reveal.fromTo(group, { opacity: 0, y: 32 }, { opacity: 1, y: 0 }, i * REVEAL_BEAT);
      });
    }
  }

  _onVisibility() {
    this.pageVisible = !document.hidden;
    this._updatePlayback();
  }
}

const sections = document.querySelectorAll('section.page-top-with-model[data-ptwm]');
sections.forEach((s) => new PageTopModel(s).init());
